import { useCallback, useEffect, useState } from "react";
import {
  Activity,
  ChevronLeft,
  ChevronRight,
  ChevronsDown,
  Download,
  Eye,
  FileText,
  Filter,
  Globe,
  LogOut,
  MapPin,
  MousePointerClick,
  Search,
  Send,
  TrendingUp,
  UserCheck,
  Users,
  X,
} from "lucide-react";
import API from "../../lib/api";

// ── IP enrichment (admin-only, on-demand) ─────────────────────────────────
// Delegates to the backend, which does reverse DNS + geolocation + decimal, so
// we get the full picture (hostname / ASN / ISP / geo) with no CSP/mixed-content
// concerns. Private/local IPs return { private: true }.
async function fetchIpInfo(ip) {
  try {
    const { data } = await API.get("/admin/visitors/ip-info", { params: { ip } });
    return data;
  } catch {
    return { error: true };
  }
}

const RANGE_OPTIONS = [
  { value: "", label: "All time" },
  { value: "24h", label: "Last 24 hrs" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
];

const BRAND_OPTIONS = [
  { value: "", label: "All brands" },
  { value: "myca", label: "myCA" },
  { value: "filemytax", label: "FileMyTax" },
];

const STATUS_OPTIONS = [
  { value: "", label: "Any status" },
  { value: "anonymous", label: "Anonymous" },
  { value: "engaged", label: "Engaged" },
  { value: "converted", label: "Converted" },
];

const PAGE_SIZE = 20;

const STATUS_STYLE = {
  anonymous: "bg-gray-100 text-gray-600",
  engaged: "bg-amber-100 text-amber-700",
  converted: "bg-green-100 text-green-700",
};

const EVENT_META = {
  pageview: { icon: Eye, label: "Viewed", color: "#6C63FF" },
  cta_click: { icon: MousePointerClick, label: "Clicked", color: "#0EA5E9" },
  form_start: { icon: FileText, label: "Started form", color: "#F59E0B" },
  form_submit: { icon: Send, label: "Submitted form", color: "#10B981" },
  scroll: { icon: ChevronsDown, label: "Scrolled", color: "#9CA3AF" },
  page_exit: { icon: LogOut, label: "Left page", color: "#E57373" },
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "–";
const fmtDur = (ms) => {
  if (ms == null) return "";
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
};

export default function VisitorFunnelPanel() {
  const [brand, setBrand] = useState("");
  const [range, setRange] = useState("30d");

  const [summary, setSummary] = useState(null);
  const [topPages, setTopPages] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // List
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [list, setList] = useState({ visitors: [], total: 0, pages: 1 });
  const [listLoading, setListLoading] = useState(true);

  // Timeline modal
  const [timeline, setTimeline] = useState(null);
  const [tlLoading, setTlLoading] = useState(false);

  // Export
  const [exporting, setExporting] = useState(false);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { brand, range };
      const [s, p, src] = await Promise.all([
        API.get("/admin/visitors/summary", { params }),
        API.get("/admin/visitors/top-pages", { params: { ...params, limit: 8 } }),
        API.get("/admin/visitors/sources", { params: { ...params, limit: 8 } }),
      ]);
      setSummary(s.data);
      setTopPages(p.data || []);
      setSources(src.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to load funnel");
    }
    setLoading(false);
  }, [brand, range]);

  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      const { data } = await API.get("/admin/visitors", {
        params: { brand, status, search, page, limit: PAGE_SIZE },
      });
      setList(data);
    } catch {
      setList({ visitors: [], total: 0, pages: 1 });
    }
    setListLoading(false);
  }, [brand, status, search, page]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);
  useEffect(() => {
    loadList();
  }, [loadList]);

  // Export the visitor list matching the current filters (loops pages, so it
  // exports everything, not just the page on screen). Capped for safety.
  const exportCsv = async () => {
    setExporting(true);
    try {
      let all = [];
      let p = 1;
      let pages = 1;
      do {
        const { data } = await API.get("/admin/visitors", {
          params: { brand, status, search, page: p, limit: 100 },
        });
        all = all.concat(data.visitors || []);
        pages = data.pages || 1;
        p += 1;
      } while (p <= pages && p <= 100); // safety cap: 10k rows

      const header = [
        "Visitor ID", "Status", "Brand", "Email", "Source", "Medium", "Campaign",
        "Landing Page", "Referrer", "Device", "IP", "Pageviews", "Sessions",
        "Converted User", "First Seen", "Last Seen",
      ];
      const rows = all.map((v) => [
        v.id, v.status, v.brand, v.email || "", v.utm_source || "", v.utm_medium || "",
        v.utm_campaign || "", v.first_landing_path || "", v.first_referrer || "",
        v.device_type || "", v.last_ip || v.first_ip || "", v.pageview_count,
        v.session_count, v.converted_email || "", v.first_seen, v.last_seen,
      ]);
      const csv = [header, ...rows]
        .map((r) => r.map((c) => `"${String(c == null ? "" : c).replace(/"/g, '""')}"`).join(","))
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `visitors-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      /* ignore — export is best-effort */
    }
    setExporting(false);
  };

  const openTimeline = async (id) => {
    setTimeline({ visitor: { id }, events: [] });
    setTlLoading(true);
    try {
      const { data } = await API.get(`/admin/visitors/${id}/timeline`);
      setTimeline(data);
    } catch {
      setTimeline(null);
    }
    setTlLoading(false);
  };

  const funnelMax = summary?.funnel?.[0]?.count || 0;

  return (
    <div className="space-y-5 min-w-0 overflow-x-hidden">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1A1A2E] via-[#2A1F5C] to-[#4F46E5] text-white px-6 py-6">
        <div className="absolute -top-20 -right-12 w-60 h-60 bg-[#A78BFA]/25 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-12 w-60 h-60 bg-[#22D3EE]/15 rounded-full blur-3xl" />
        <div className="relative flex items-end justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur-sm border border-white/15 flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-heading font-bold leading-tight">
                Visitor Funnel
              </h1>
              <p className="text-sm text-white/75 font-body mt-0.5">
                Anonymous visitors → leads → conversions
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              className="text-sm font-body rounded-lg px-3 py-2 bg-white/10 border border-white/20 text-white focus:outline-none [&>option]:text-[#1A1A2E]"
            >
              {BRAND_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <select
              value={range}
              onChange={(e) => setRange(e.target.value)}
              className="text-sm font-body rounded-lg px-3 py-2 bg-white/10 border border-white/20 text-white focus:outline-none [&>option]:text-[#1A1A2E]"
            >
              {RANGE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-600 font-body">
          {error}{" "}
          <button onClick={loadAnalytics} className="underline">Retry</button>
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={Users} label="Visitors" value={summary?.total_visitors} accent="#6C63FF" loading={loading} />
        <KpiCard icon={Activity} label="Engaged" value={summary?.engaged} accent="#0EA5E9" loading={loading} />
        <KpiCard icon={UserCheck} label="Converted" value={summary?.converted} accent="#10B981"
          footer={summary ? `${summary.conversion_rate}% conversion` : ""} loading={loading} />
        <KpiCard icon={Eye} label="Pageviews" value={summary?.total_pageviews} accent="#F59E0B"
          footer={summary ? `${summary.total_sessions} sessions` : ""} loading={loading} />
      </div>

      {/* Funnel + sources */}
      <div className="grid lg:grid-cols-5 gap-5">
        {/* Funnel bars */}
        <div className="lg:col-span-3 bg-white rounded-2xl border border-[#E8E6F8] shadow-sm p-5">
          <h2 className="text-sm font-heading font-bold text-[#1A1A2E] mb-4">Conversion Funnel</h2>
          {!summary || funnelMax === 0 ? (
            <EmptyMini label="No visitors in this period yet" />
          ) : (
            <div className="space-y-3">
              {summary.funnel.map((s, i) => {
                const pct = funnelMax ? Math.round((s.count / funnelMax) * 100) : 0;
                const tints = ["#6C63FF", "#0EA5E9", "#F59E0B", "#10B981"];
                return (
                  <div key={s.stage}>
                    <div className="flex items-center justify-between text-xs font-body mb-1">
                      <span className="text-[#1A1A2E] font-medium">{s.stage}</span>
                      <span className="text-[#6B7280]">
                        {s.count.toLocaleString("en-IN")}{" "}
                        <span className="text-[#9CA3AF]">({pct}%)</span>
                      </span>
                    </div>
                    <div className="w-full h-3 bg-[#F5F4FB] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${pct}%`, backgroundColor: tints[i] }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top sources */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-[#E8E6F8] shadow-sm p-5">
          <h2 className="text-sm font-heading font-bold text-[#1A1A2E] mb-4">Top Sources</h2>
          {sources.length === 0 ? (
            <EmptyMini label="No source data yet" />
          ) : (
            <div className="space-y-2.5">
              {sources.map((s) => (
                <div key={s.source} className="flex items-center justify-between gap-2 text-sm font-body">
                  <span className="flex items-center gap-2 text-[#1A1A2E] truncate min-w-0">
                    <Globe className="h-3.5 w-3.5 text-[#9CA3AF] flex-shrink-0" />
                    <span className="truncate">{s.source}</span>
                  </span>
                  <span className="text-[#6B7280] flex-shrink-0 ml-2">
                    {s.visitors}
                    {s.conversions > 0 && (
                      <span className="text-green-600 font-medium"> · {s.conversions}✓</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Top pages */}
      <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm p-5">
        <h2 className="text-sm font-heading font-bold text-[#1A1A2E] mb-4">Top Pages</h2>
        {topPages.length === 0 ? (
          <EmptyMini label="No pageviews yet" />
        ) : (
          <div className="divide-y divide-[#F0EEF9]">
            {topPages.map((p) => (
              <div key={p.path} className="flex items-center justify-between gap-3 py-2 text-sm font-body">
                <span
                  className="text-[#1A1A2E] truncate font-mono text-xs min-w-0 flex-1"
                  title={p.path}
                >
                  {p.path}
                </span>
                <span className="text-[#6B7280] flex-shrink-0 whitespace-nowrap">
                  {p.views} views · {p.visitors} visitors
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Visitor list */}
      <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E8E6F8] flex flex-wrap items-center gap-3 justify-between">
          <h2 className="text-sm font-heading font-bold text-[#1A1A2E]">
            Visitors <span className="text-[#9CA3AF] font-body font-normal">({list.total})</span>
          </h2>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9CA3AF]" />
              <input
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search id / source / page / email…"
                className="pl-8 pr-3 py-1.5 text-xs font-body border border-[#E8E6F8] rounded-lg bg-[#F5F4FB] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50 w-[240px]"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9CA3AF]" />
              <select
                value={status}
                onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                className="pl-8 pr-3 py-1.5 text-xs font-body border border-[#E8E6F8] rounded-lg bg-[#F5F4FB] focus:outline-none"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <button
              onClick={exportCsv}
              disabled={exporting || list.total === 0}
              className="flex items-center gap-1.5 text-xs font-body text-[#6C63FF] border border-[#6C63FF]/30 hover:bg-[#6C63FF]/10 px-3 py-1.5 rounded-lg disabled:opacity-40 transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              {exporting ? "Exporting…" : "Export CSV"}
            </button>
          </div>
        </div>

        {listLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-[#6C63FF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : list.visitors.length === 0 ? (
          <p className="text-center text-sm text-[#9CA3AF] font-body py-10">No visitors match.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-body">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-[#9CA3AF] border-b border-[#E8E6F8]">
                  <th className="text-left font-medium px-5 py-2.5">Status</th>
                  <th className="text-left font-medium px-3 py-2.5">Source</th>
                  <th className="text-left font-medium px-3 py-2.5">IP</th>
                  <th className="text-left font-medium px-3 py-2.5">Landing</th>
                  <th className="text-left font-medium px-3 py-2.5">Views</th>
                  <th className="text-left font-medium px-3 py-2.5">Converted</th>
                  <th className="text-left font-medium px-3 py-2.5">Last seen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F0EEF9]">
                {list.visitors.map((v) => (
                  <tr
                    key={v.id}
                    onClick={() => openTimeline(v.id)}
                    className="hover:bg-[#F5F4FB] cursor-pointer transition-colors"
                  >
                    <td className="px-5 py-3">
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${STATUS_STYLE[v.status] || "bg-gray-100 text-gray-600"}`}>
                        {v.status}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-[#6B7280]">{v.utm_source || "direct"}</td>
                    <td className="px-3 py-3 text-[#6B7280] font-mono text-xs">{v.last_ip || v.first_ip || "–"}</td>
                    <td className="px-3 py-3 text-[#6B7280] font-mono text-xs truncate max-w-[180px]">{v.first_landing_path || "–"}</td>
                    <td className="px-3 py-3 text-[#1A1A2E]">{v.pageview_count}</td>
                    <td className="px-3 py-3">
                      {v.converted_email ? (
                        <span className="text-green-600 text-xs">{v.converted_email}</span>
                      ) : (
                        <span className="text-[#D1D5DB]">–</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-[#9CA3AF] text-xs whitespace-nowrap">{fmtDate(v.last_seen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {list.pages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-[#E8E6F8]">
            <span className="text-xs text-[#6B7280] font-body">Page {page} / {list.pages}</span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="flex items-center gap-1 text-xs font-body px-3 py-1.5 border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] disabled:opacity-40"
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Prev
              </button>
              <button
                onClick={() => setPage((p) => Math.min(list.pages, p + 1))}
                disabled={page >= list.pages}
                className="flex items-center gap-1 text-xs font-body px-3 py-1.5 border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] disabled:opacity-40"
              >
                Next <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {timeline && (
        <TimelineModal
          data={timeline}
          loading={tlLoading}
          onClose={() => setTimeline(null)}
        />
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, accent, footer, loading }) {
  return (
    <div className="relative overflow-hidden rounded-2xl bg-white border border-[#E8E6F8] p-5 shadow-sm">
      <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full opacity-[0.08] blur-2xl" style={{ backgroundColor: accent }} />
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${accent}18` }}>
        <Icon className="h-5 w-5" style={{ color: accent }} />
      </div>
      <p className="text-[11px] font-body font-medium text-[#6B7280] mt-4 uppercase tracking-wider">{label}</p>
      <p className="text-[28px] font-heading font-bold text-[#1A1A2E] leading-tight mt-1">
        {loading ? "…" : (value ?? 0).toLocaleString("en-IN")}
      </p>
      {footer && <p className="text-[11px] font-body text-[#9CA3AF] mt-1">{footer}</p>}
    </div>
  );
}

function EmptyMini({ label }) {
  return <p className="text-sm text-[#9CA3AF] font-body py-8 text-center">{label}</p>;
}

function TimelineModal({ data, loading, onClose }) {
  const v = data.visitor || {};
  const ip = v.last_ip || v.first_ip || null;
  const [ipInfo, setIpInfo] = useState(null);

  useEffect(() => {
    if (!ip) {
      setIpInfo(null);
      return;
    }
    let active = true;
    setIpInfo({ loading: true });
    fetchIpInfo(ip).then((res) => active && setIpInfo(res));
    return () => {
      active = false;
    };
  }, [ip]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-[#E8E6F8] flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="text-sm font-heading font-bold text-[#1A1A2E]">Visitor Timeline</h3>
            <p className="text-[11px] text-[#9CA3AF] font-body font-mono truncate">{v.id}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs font-body text-[#6B7280]">
              <span>Status: <strong className="text-[#1A1A2E]">{v.status}</strong></span>
              <span>Source: <strong className="text-[#1A1A2E]">{v.utm_source || "direct"}</strong></span>
              <span>Device: <strong className="text-[#1A1A2E]">{v.device_type || "–"}</strong></span>
              {v.converted_email && <span className="text-green-600">→ {v.converted_email}</span>}
            </div>
            <div className="mt-2 text-xs font-body">
              <span className="inline-flex items-center gap-1 text-[#6B7280]">
                <MapPin className="h-3 w-3 text-[#9CA3AF]" />
                IP: <strong className="text-[#1A1A2E]">{ip || "–"}</strong>
                {ipInfo?.loading && <span className="text-[#9CA3AF] ml-1">· looking up…</span>}
                {ipInfo?.private && <span className="text-[#9CA3AF] ml-1">· Local / private IP</span>}
                {ipInfo?.error && <span className="text-[#9CA3AF] ml-1">· details unavailable</span>}
                {ipInfo?.flag && <span className="ml-1">{ipInfo.flag}</span>}
              </span>
              {ipInfo && !ipInfo.loading && !ipInfo.private && !ipInfo.error && (
                <>
                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 bg-[#F5F4FB] rounded-lg p-3">
                    {[
                      ["Hostname", ipInfo.hostname],
                      ["ASN", ipInfo.asn],
                      ["ISP", ipInfo.isp],
                      ["Organisation", ipInfo.org],
                      ["Country", ipInfo.country],
                      ["State / Region", ipInfo.region],
                      ["City", ipInfo.city],
                      ["Latitude", ipInfo.lat],
                      ["Longitude", ipInfo.lon],
                      ["Decimal", ipInfo.decimal],
                    ].map(([label, val]) => (
                      <div key={label} className="min-w-0">
                        <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF]">{label}</p>
                        <p className="text-[#1A1A2E] truncate" title={val == null ? "" : String(val)}>
                          {val == null || val === "" ? "–" : String(val)}
                        </p>
                      </div>
                    ))}
                  </div>
                  {ipInfo.lat != null && ipInfo.lon != null && (
                    <a
                      href={`https://www.google.com/maps?q=${ipInfo.lat},${ipInfo.lon}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-1.5 text-[11px] text-[#6C63FF] hover:underline"
                    >
                      View on map →
                    </a>
                  )}
                </>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#1A1A2E]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-[#6C63FF] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (data.events || []).length === 0 ? (
            <EmptyMini label="No events recorded" />
          ) : (
            <ol className="relative border-l-2 border-[#E8E6F8] ml-2 space-y-4">
              {data.events.map((ev) => {
                const meta = EVENT_META[ev.event_type] || { icon: Activity, label: ev.event_type, color: "#9CA3AF" };
                const Icon = meta.icon;
                return (
                  <li key={ev.id} className="ml-5">
                    <span
                      className="absolute -left-[11px] w-5 h-5 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: `${meta.color}22` }}
                    >
                      <Icon className="h-3 w-3" style={{ color: meta.color }} />
                    </span>
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-body text-[#1A1A2E]">
                        <span className="font-medium">{meta.label}</span>
                        {ev.event_label && <span className="text-[#6B7280]"> · {ev.event_label}</span>}
                      </p>
                      <span className="text-[10px] text-[#9CA3AF] font-body whitespace-nowrap">
                        {fmtDate(ev.created_at)}
                      </span>
                    </div>
                    {ev.path && (
                      <p className="text-xs text-[#9CA3AF] font-mono truncate">
                        {ev.path}
                        {ev.duration_ms != null && ev.event_type !== "pageview" ? "" :
                          ev.duration_ms ? ` · ${fmtDur(ev.duration_ms)}` : ""}
                      </p>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
